// System
import Cookies from 'js-cookie';
import ReactDOM from 'react-dom/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
  fromPromise,
} from '@apollo/client';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { useMemo } from 'react';
import { App } from './app/App';
import { RootContainer } from '@Components/root/RootContainer';
import { store, persistor } from './app/store';
import { StoreInterface } from './app/types';

export const ApolloProviderWithRgs = ({ children }: any) => {
  const rgs = useSelector((state: StoreInterface) => state.general.rgs) || 'EU';
  const game = useSelector((state: StoreInterface) => state.general.game) || 2;

  const client = useMemo(() => {
    const httpLink = createHttpLink({
      uri: process.env.REACT_APP_GRAPHQL_API_PROXY_URL,
    });

    const authLink = setContext((operation, { headers }) => {
      const token = Cookies.get('accessToken');
      const { operationName } = operation;

      const baseHeaders = {
        ...headers,
        'x-rgs-id': rgs,
        authorization: token ? `Bearer ${token}` : '',
      };

      if (operationName !== 'getProviders') {
        baseHeaders['x-game-id'] = game;
      }

      return { headers: baseHeaders };
    });

    // Error link to handle token refresh
    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        for (const err of graphQLErrors) {
          if (err.extensions?.code === 'UNAUTHENTICATED') {
            const accessToken = Cookies.get('accessToken');
            const refreshToken = Cookies.get('refreshToken');

            // If there's no access token at all, user is not logged in - don't try to refresh
            if (!accessToken) {
              return;
            }

            // If there's no refresh token, redirect to login
            if (!refreshToken) {
              console.log('No refresh token available, calling logout and redirecting to login');

              // Call logout mutation to inform backend
              fetch(process.env.REACT_APP_GRAPHQL_API_PROXY_URL || '', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'x-rgs-id': rgs,
                  'x-game-id': game.toString(),
                  authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                  query: `
                    mutation logout {
                      logout
                    }
                  `,
                }),
              })
                .catch(() => {
                  // Ignore logout errors - we're already cleaning up
                })
                .finally(() => {
                  // Always clean up cookies and redirect
                  Cookies.remove('accessToken');
                  Cookies.remove('refreshToken');
                  window.location.href = '/';
                });

              return;
            }
            // Attempt to refresh the token
            return fromPromise(
              fetch(process.env.REACT_APP_GRAPHQL_API_PROXY_URL || '', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'x-rgs-id': rgs,
                  'x-game-id': game.toString(),
                  authorization: `Bearer ${refreshToken}`,
                },
                body: JSON.stringify({
                  query: `
                    mutation refreshToken($token: String!) {
                      refreshToken(token: $token) {
                        accessToken
                        refreshToken
                      }
                    }
                  `,
                  variables: { token: refreshToken },
                }),
              })
                .then((response) => response.json())
                .then((response) => {
                  const newAccessToken = response.data?.refreshToken?.accessToken;
                  const newRefreshToken = response.data?.refreshToken?.refreshToken;

                  if (newAccessToken && newRefreshToken) {
                    // Update cookies with new tokens
                    Cookies.set('accessToken', newAccessToken);
                    Cookies.set('refreshToken', newRefreshToken);

                    // Retry the original operation with new token
                    const oldHeaders = operation.getContext().headers;
                    operation.setContext({
                      headers: {
                        ...oldHeaders,
                        authorization: `Bearer ${newAccessToken}`,
                      },
                    });
                  } else {
                    console.log('Token refresh failed, calling logout and redirecting to login');

                    // Call logout mutation to inform backend
                    fetch(process.env.REACT_APP_GRAPHQL_API_PROXY_URL || '', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'x-rgs-id': rgs,
                        'x-game-id': game.toString(),
                        authorization: `Bearer ${accessToken}`,
                      },
                      body: JSON.stringify({
                        query: `
                          mutation logout {
                            logout
                          }
                        `,
                      }),
                    })
                      .catch(() => {
                        // Ignore logout errors - we're already cleaning up
                      })
                      .finally(() => {
                        // Always clean up cookies and redirect
                        Cookies.remove('accessToken');
                        Cookies.remove('refreshToken');
                        window.location.href = '/';
                      });
                  }
                })
                .catch((error) => {
                  console.log('Token refresh request failed:', error);

                  // Call logout mutation to inform backend
                  fetch(process.env.REACT_APP_GRAPHQL_API_PROXY_URL || '', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'x-rgs-id': rgs,
                      'x-game-id': game.toString(),
                      authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                      query: `
                        mutation logout {
                          logout
                        }
                      `,
                    }),
                  })
                    .catch(() => {
                      // Ignore logout errors - we're already cleaning up
                    })
                    .finally(() => {
                      // Always clean up cookies and redirect
                      Cookies.remove('accessToken');
                      Cookies.remove('refreshToken');
                      window.location.href = '/';
                    });
                })
            ).flatMap(() => forward(operation));
          }
        }
      }

      if (networkError) {
        console.error('Network error:', networkError);
      }
    });

    return new ApolloClient({
      link: authLink.concat(errorLink.concat(httpLink)),
      cache: new InMemoryCache(),
    });
  }, [rgs, game]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

const jsx = (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <ApolloProviderWithRgs>
        <RootContainer>
          <App />
        </RootContainer>
      </ApolloProviderWithRgs>
    </PersistGate>
  </Provider>
);

const rootElement = document.getElementById('root');

if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(jsx);
}
